import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import LegalDocument from '@/components/LegalDocument'

interface ContractAgreementModalProps {
  onAgree: () => Promise<void>
  onDecline: () => void
}

export interface ContractAgreementModalRef {
  open: () => void
  close: () => void
}

const ContractAgreementModal = forwardRef<ContractAgreementModalRef, ContractAgreementModalProps>(
  ({ onAgree, onDecline }, ref) => {
    const { t } = useTranslation('common')
    const [isOpen, setIsOpen] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false)
    const [error, setError] = useState('')

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsOpen(true)
        setError('')
      },
      close: () => {
        if (!isProcessing) {
          setIsOpen(false)
        }
      },
    }))

    const handleAgree = async () => {
      try {
        setIsProcessing(true)
        setError('')
        await onAgree()
        setIsOpen(false)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to agree to terms'
        setError(errorMessage)
      } finally {
        setIsProcessing(false)
      }
    }

    const handleDecline = () => {
      if (!isProcessing) {
        onDecline()
        setIsOpen(false)
      }
    }

    if (!isOpen) return null

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-primary/5 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-sm">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {t('contractAgreement.title', 'Terms and Conditions')}
                </h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  {t('contractAgreement.subtitle', 'Please review and accept to continue')}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5">
                <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700 flex-1">{error}</p>
              </div>
            )}

            <LegalDocument document="terms" />

            {/* Important Notice */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">
                    {t('contractAgreement.notice', 'Important Notice')}
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    {t('contractAgreement.noticeText', 'You must agree to these terms to continue using the platform. If you decline, you will be logged out immediately.')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50/50 flex items-center justify-end gap-3">
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="mr-auto text-sm text-gray-500 hover:text-primary transition-colors"
            >
              {t('contractAgreement.openFullText', 'Open in a new tab')}
            </Link>
            <button
              onClick={handleDecline}
              disabled={isProcessing}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {t('contractAgreement.decline', 'Decline & Logout')}
            </button>
            <button
              onClick={handleAgree}
              disabled={isProcessing}
              className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-sm hover:shadow"
            >
              {isProcessing && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>{isProcessing ? t('contractAgreement.agreeing', 'Agreeing...') : t('contractAgreement.agree', 'I Agree')}</span>
            </button>
          </div>
        </div>
      </div>
    )
  }
)

ContractAgreementModal.displayName = 'ContractAgreementModal'

export default ContractAgreementModal
